import { useEffect, useCallback, useRef } from 'react';
import { useWebSocketContext } from '../contexts/WebSocketContext';
import { useMediaStore } from '@brightmls/nextgen.lae.mediacenter';

export interface MediaJob {
  jobId: string;
  listingKey: string;
  listingId?: string;
  status:
    | 'pending'
    | 'queued'
    | 'processing'
    | 'submitting'
    | 'submitted'
    | 'completed'
    | 'failed';
  aiStatus?:
    | 'pending'
    | 'scanning'
    | 'scanned'
    | 'applying'
    | 'applied'
    | 'failed'
    | 'skipped';
  failedStep?: 'submission' | 'scan' | 'apply';
  isAIError?: boolean; // True if this is an AI-specific error (submission succeeded)
  fileName: string;
  fileSize?: number;
  category: string;
  previewUrl?: string; // Presigned URL from S3
  docType?: string;
  processedUrl?: string; // Presigned URL for resized image
  resultUrl?: string; // Final result URL
  mediaKey?: string; // MLS media key (after submission)
  mediaItemNumber?: number;
  mediaDisplayOrder?: number;
  visibility?: string;
  error?: string;
  canRetry?: boolean;
  progress?: number; // 0-100
  retryCount?: number;
  aiRetryCount?: number;
  scanForObjects?: boolean;
  generateDescriptions?: boolean;
  /** Final media record from server on completion - can be added directly to FLC */
  finalMediaRecord?: {
    MED_VW_KEY: string;
    MEDIA_KEY: string;
    MEDIA_CATEGORY: string;
    MEDIA_DISPLAY_ORDER: number;
    MEDIA_ITEM_NUMBER: number;
    MEDIA_SHORT_DESCRIPTION?: string;
    MEDIA_LONG_DESCRIPTION?: string;
    MEDIA_URL?: string;
    MEDIA_URL_MEDIUM?: string;
    MEDIA_IMAGE_WIDTH?: number;
    MEDIA_IMAGE_HEIGHT?: number;
    MEDIA_VISIBILITY?: string;
    MEDIA_MODIFICATION_TIMESTAMP?: string;
    [key: string]: any;
  };
  imageMetadata?: {
    width?: number;
    height?: number;
    format?: string;
    orientation?: number;
    size?: number;
  };
  pdfMetadata?: {
    pageCount?: number;
    size?: number;
  };
  aiResults?: {
    listing_id?: string;
    image_url?: string;
    tags?: Array<{
      label: string;
      bounding_box?: number[];
      centroid?: number[];
      score?: number;
    }>;
    labels?: Array<{
      label: string;
      category?: string;
      score?: number;
    }>;
    image_description?: {
      description?: string;
      compliance?: Record<string, any>;
      internal_caption?: string;
    };
    // Legacy format support
    objects?: string[];
    description?: string;
  };
  createdAt: string;
  updatedAt?: string;
  completedAt?: string; // Maps to processingCompletedAt from backend
}

export interface MediaSubscriptionOptions {
  listingKey: string;
  enabled: boolean;
  reconcile?: boolean; // Default: true (safer for reconnects)
  onJobUpdate?: (job: MediaJob) => void;
  onError?: (error: Error) => void;
}

export function useMediaSubscription({
  listingKey,
  enabled,
  reconcile = true,
  onJobUpdate,
  onError,
}: MediaSubscriptionOptions) {
  const { sendMessageAndWait, isConnected, addGlobalListener } =
    useWebSocketContext();
  const updateProcessingItem = useMediaStore(
    (state) => state.updateProcessingItem,
  );
  const hasSubscribed = useRef(false);
  const hasReconciledOnce = useRef(false);

  /**
   * Subscribe to media updates for this listing
   */
  const subscribe = useCallback(async () => {
    if (!isConnected || !enabled || hasSubscribed.current) {
      return;
    }

    // Set flag immediately to prevent concurrent calls from entering
    hasSubscribed.current = true;

    try {
      // Only use reconcile=true once per hook lifetime to avoid duplicate reconciliation
      const shouldReconcile = reconcile && !hasReconciledOnce.current;

      // Set flag immediately before await to prevent concurrent calls from also reconciling
      if (shouldReconcile) {
        hasReconciledOnce.current = true;
      }

      const response = await sendMessageAndWait<{
        subscribed: boolean;
        listingKey: string;
        jobs?: MediaJob[];
      }>('MEDIA.SUBSCRIBE', {
        listingKey,
        reconcile: shouldReconcile,
      });

      if (response.subscribed) {
        // If reconcile mode, update store with current jobs (excluding "pending")
        if (shouldReconcile && response.jobs && response.jobs.length > 0) {
          // Ignore for now: we don't want to overwrite existing processing items
          // updateProcessingItem(response.jobs.filter(job => job.status !== 'pending'))
        }
      } else {
        // Subscription not confirmed, allow retry
        hasSubscribed.current = false;
      }
    } catch (error) {
      // Reset hasSubscribed to allow retry, but keep hasReconciledOnce to prevent duplicate reconcile
      hasSubscribed.current = false;
      console.error('[useMediaSubscription] Failed to subscribe:', error);
      onError?.(error as Error);
    }
  }, [
    isConnected,
    enabled,
    listingKey,
    reconcile,
    sendMessageAndWait,
    updateProcessingItem,
    onJobUpdate,
    onError,
  ]);

  /**
   * Handle broadcast messages from DynamoDB Stream (media-process-notify)
   *
   * Handles three types of broadcasts:
   * - MEDIA_UPDATE: General job updates (queued, processing, etc.)
   * - MEDIA_ERROR: Job failures
   * - MEDIA_COMPLETE: Job completions
   */
  const handleBroadcast = useCallback(
    (message: any) => {
      // Check if this is a media broadcast
      if (message.type !== 'response') {
        return;
      }

      // Handle all broadcast types
      const isBroadcast =
        message.requestId === 'MEDIA_UPDATE' ||
        message.requestId === 'MEDIA_ERROR' ||
        message.requestId === 'MEDIA_COMPLETE' ||
        message.requestId === 'MEDIA_AI_UPDATE';

      if (!isBroadcast) {
        return;
      }

      // Extract job from the various event types
      const job = message.data?.job as MediaJob;
      if (!job) {
        return;
      }

      // Only process if it's for our listing
      if (job.listingKey !== listingKey) {
        return;
      }

      // Notify callback
      onJobUpdate?.(job);
    },
    [listingKey, updateProcessingItem, onJobUpdate],
  );

  /**
   * Set up WebSocket message listener for broadcasts
   */
  useEffect(() => {
    if (!enabled || !isConnected) {
      return;
    }

    const cleanup = addGlobalListener(handleBroadcast);

    return cleanup;
  }, [enabled, isConnected, addGlobalListener, handleBroadcast]);

  /**
   * Subscribe when connection is established
   */
  useEffect(() => {
    if (isConnected && enabled && !hasSubscribed.current) {
      subscribe();
    }

    // Reset subscription flag on disconnect
    if (!isConnected) {
      hasSubscribed.current = false;
    }
  }, [isConnected, enabled, subscribe]);

  /**
   * Unsubscribe on unmount
   */
  useEffect(() => {
    return () => {
      hasSubscribed.current = false;
    };
  }, []);

  return {
    isSubscribed: hasSubscribed.current,
    subscribe,
  };
}
